<template>
	<div class="admin-actions">
		<div class="activate-admin-settings">
			<Button
				label="Admin Settings"
				color="black"
				icon="cog"
				@click.native="openSettings"
				/>
		</div>
		<div 
			class="admin-action-box"
			:class="[
				settingsOpen ? 'show-settings' : ''
			]"
			>

			<div class="nav-bar">

				<div 
					class="back-btn" 
					:class="[
						settingsPanelOpen ? 'show-back' : ''
					]"
					@click="goBack"
					>
					<i class="fal fa-chevron-left" />
				</div>
				<div class="close-btn" @click="closeSettings" />

			</div>

			<div 
				:class="[
					!settingsPanelOpen ? 'show-buttons' : ''
				]"
				class="buttons"
				>

				<div class="main-btns">
					<div
						v-if="hasPermission('caseAdminNotificationSettings')"
						class="button"
						:class="[
							selectedSetting == 'notificationSettings' ? 'active' : ''
						]"
						@click="selectSetting('notificationSettings', true)"
						>
						Notification Settings
					</div>
					<div
						v-if="hasPermission('caseAdminViewers')"
						class="button"
						:class="[
							selectedSetting == 'viewers' ? 'active' : ''
						]"
						@click="selectSetting('viewers', true)"
						>
						Viewers <span class="counter">{{ thisCase.viewers.length }}</span>
					</div>
					<div
						v-if="hasPermission('caseAdminEditors')"
						class="button"
						:class="[
							selectedSetting == 'editors' ? 'active' : ''
						]"
						@click="selectSetting('editors', true)"
						>
						Editors / Supporting Surgeons <span class="counter">{{ thisCase.editors.length }}</span>
					</div>
					<div
						v-if="hasPermission('caseAdminSetStage')"
						class="button"
						:class="[
							selectedSetting == 'setStage' ? 'active' : ''
						]"
						@click="selectSetting('setStage', true)"
						>
						Set Stage
					</div>
					<div
						v-if="hasPermission('caseAdminCaseSettings')"
						class="button"
						:class="[
							selectedSetting == 'caseSettings' ? 'active' : '',
							thisCase.progress_stage == 1 ? 'current-stage' : '',
							thisCase.progress_stage > 1 ? 'completed-stage' : '',
						]"
						@click="selectSetting('caseSettings', true)"
						>
						Case Settings
					</div>
					<div
						v-if="hasPermission('caseAdminDesignMeeting')"
						class="button"
						:class="[
							selectedSetting == 'designMeeting' ? 'active' : '',
							thisCase.progress_stage < 2 ? 'disabled' : '',
							thisCase.progress_stage == 2 ? 'current-stage' : '',
							thisCase.progress_stage > 2 ? 'completed-stage' : '',
						]"
						@click="selectSetting('designMeeting', thisCase.progress_stage >= 2)"
						>
						Design Meeting Settings
					</div>
					<div
						v-if="hasPermission('caseAdminTreatmentPlan')"
						class="button"
						:class="[
							selectedSetting == 'treatmentPlan' ? 'active' : '',
							thisCase.progress_stage < 3 ? 'disabled' : '',
							thisCase.progress_stage == 3 ? 'current-stage' : '',
							thisCase.progress_stage > 3 ? 'completed-stage' : '',
						]"
						@click="selectSetting('treatmentPlan', thisCase.progress_stage >= 3)"
						>
						Treatment Plan Settings
					</div>
					<div
						v-if="hasPermission('caseAdminImplantProduction')"
						class="button"
						:class="[
							selectedSetting == 'implantProduction' ? 'active' : '',
							thisCase.progress_stage < 4 ? 'disabled' : '',
							thisCase.progress_stage == 4 ? 'current-stage' : '',
							thisCase.progress_stage > 4 ? 'completed-stage' : '',
						]"
						@click="selectSetting('implantProduction', thisCase.progress_stage >= 4)"
						>
						Implant Production Settings
					</div>
					<div
						v-if="hasPermission('canUpdatePMSSettings')"
						class="button"
						:class="[
							selectedSetting == 'pms' ? 'active' : '',
							thisCase.progress_stage < 5 ? 'disabled' : '',
							thisCase.progress_stage == 5 ? 'current-stage' : '',
							thisCase.progress_stage > 5 ? 'completed-stage' : '',
						]"
						@click="selectSetting('pms', thisCase.progress_stage >= 5)"
						>
						PMS Settings
					</div>

					<div
						class="button delete-case-btn"
						v-if="thisCase.progress_stage < 1"
						@click="deleteCase"
						>
						Delete Case
					</div>

					<div
						class="button delete-case-btn"
						@click="archiveCase"
						v-if="thisCase.progress_stage > 0 && thisCase.status != 'archive'"
						>
						Archive Case
					</div>


				</div>

				
			</div>

			<div
				:class="[
					settingsPanelOpen ? 'show-panel' : ''
				]" 
				class="settings-panels"
				>

				<div class="inputs" v-if="selectedSetting == 'notificationSettings' && hasPermission('caseAdminNotificationSettings')">
					<AdminBoxNotifications :thisCase="thisCase" :notifiableUsers="notifiableUsers" />
				</div>
				<div class="inputs" v-if="selectedSetting == 'viewers' && hasPermission('caseAdminNotificationSettings')">
					<AdminBoxViewers :thisCase="thisCase" :viewers="viewers" />
				</div>
				<div class="inputs" v-if="selectedSetting == 'editors' && hasPermission('caseAdminEditors')">
					<AdminBoxEditors :thisCase="thisCase" :editors="editors" />
				</div>
				<div class="inputs" v-if="selectedSetting == 'caseSettings' && hasPermission('caseAdminCaseSettings')">
					<AdminBoxCaseSettings :thisCase="thisCase" :availableUsers="availableUsers" />
				</div>
				<div class="inputs" v-if="selectedSetting == 'designMeeting' && hasPermission('caseAdminDesignMeeting')">
					<AdminBoxDesignMeeting :thisCase="thisCase" />
				</div>
				<div class="inputs" v-if="selectedSetting == 'treatmentPlan' && hasPermission('caseAdminTreatmentPlan')">
					<AdminBoxTreatmentPlan :thisCase="thisCase" />
				</div>
				<div class="inputs" v-if="selectedSetting == 'implantProduction' && hasPermission('caseAdminImplantProduction')">
					<AdminBoxProduction :thisCase="thisCase" />
				</div>
				<div class="inputs" v-if="selectedSetting == 'setStage' && hasPermission('caseAdminSetStage')">
					<AdminBoxSetStage :thisCase="thisCase" />
				</div>
				<div class="inputs" v-if="selectedSetting == 'pms' && hasPermission('canUpdatePMSSettings')">
					<AdminBoxPMS 
						:thisCase="thisCase" 
						:anatomies="anatomies"
						:techFiles="techFiles"
						:caseFeatures="caseFeatures"
						/>
				</div>

				<div class="pad-bottom-for-fab" />

			</div>


		</div>
	</div>
</template>

<script>

import Button from "@/Components/Button"
import AdminBoxViewers from "@/Components/Admin/AdminBoxViewers"
import AdminBoxEditors from "@/Components/Admin/AdminBoxEditors"
import AdminBoxCaseSettings from "@/Components/Admin/AdminBoxCaseSettings"
import AdminBoxDesignMeeting from "@/Components/Admin/AdminBoxDesignMeeting"
import AdminBoxTreatmentPlan from "@/Components/Admin/AdminBoxTreatmentPlan"
import AdminBoxProduction from "@/Components/Admin/AdminBoxProduction"
import AdminBoxSetStage from "@/Components/Admin/AdminBoxSetStage"
import AdminBoxPMS from "@/Components/Admin/AdminBoxPMS"
import AdminBoxNotifications from "@/Components/Admin/AdminBoxNotifications"

export default {
	props: {
		thisCase: {
			type: Object,
			required: true
		},
		viewers: {
			type: Array,
			required: true
		},
		editors: {
			type: Array,
			required: true
		},
		availableUsers: {
			type: Array,
			required: true
		},
		permissions: {
			type: Object,
			required: true
		},
		anatomies: {
			type: [Array, Boolean],
			required: false,
			default: false
		},
		techFiles: {
			type: [Array, Boolean],
			required: false,
			default: false
		},
		caseFeatures: {
			type: [Array, Boolean],
			required: false,
			default: false
		},
		notifiableUsers: {
			type: Array,
			required: false
		}
	},
	components: {
		Button,
		AdminBoxViewers,
		AdminBoxEditors,
		AdminBoxCaseSettings,
		AdminBoxDesignMeeting,
		AdminBoxTreatmentPlan,
		AdminBoxProduction,
		AdminBoxSetStage,
		AdminBoxPMS,
		AdminBoxNotifications
	},
	data: () => {
		return {
			settingsOpen: false,
			selectedSetting: '',
			settingsPanelOpen: false,
		}
	},
	mounted(){
		// temp for testing/development
		// this.openSettings()
		// this.selectSetting('notificationSettings', true)

		// console.log('permissions', this.permissions)

	},
	methods: {
		openSettings(){
			// console.log('settings open')
			this.settingsOpen = true
		},
		closeSettings(){
			this.selectedSetting = ''
			this.settingsOpen = false
			this.settingsPanelOpen = false
		},
		goBack(){
			this.selectedSetting = ''
			this.settingsPanelOpen = false
		},
		archiveCase(){
			if(confirm('Are you sure you wish to archive this case?')){
				this.$inertia.post(`/admin/cases/${this.thisCase.hashid}`, {
					status: 'archive'
				})
			}
		},
		selectSetting(setting, allowed){
			if(allowed){
				this.selectedSetting = setting
				this.settingsPanelOpen = true
			}
		},
		deleteCase(){
			let confirm = window.confirm('Are you sure you want to delete this case? This can\'t be undone')
			if(confirm){
				this.$inertia.post(`/admin/cases/${this.thisCase.hashid}/delete`)
			}
		},
		hasPermission(permission){

			const result = typeof this.permissions === 'object' 
				&& this.permissions !== null 
				&& this.permissions.hasOwnProperty(permission)
				&& this.permissions[permission] === true

			return result

		}
	},
	// watch: {
	//   '$page.props.flash.message': function (payload) {
	//     this.selectedSetting = ''
	//   }
	// }
}

</script>

<style scoped lang="sass">

.activate-admin-settings
	z-index: 9
	position: relative

.admin-action-box
	background-color: #181818
	display: flex
	box-shadow: 0 1px 3px rgba(#000, 0.2)
	flex-direction: column
	z-index: 0
	position: fixed
	top: 80px
	right: 0
	bottom: 0
	width: 100%
	max-width: 600px
	transition: all 300ms
	visibility: hidden
	opacity: 0
	transform: translateX(100%)

	@media (max-width: 768px)
		top: 60px

	&.show-settings
		visibility: visible
		opacity: 1
		z-index: 30
		transform: translateX(0)

	.pad-bottom-for-fab
		height: 100px
		width: 100%

	.nav-bar
		width: 100%
		height: 60px
		display: flex
		align-items: center
		justify-content: space-between
		padding: 0 10px

		.close-btn
			width: 40px
			height: 40px
			position: relative
			display: flex
			align-items: center
			justify-content: center
			cursor: pointer

			&:before, &:after
				content: ''
				width: 70%
				height: 2px
				background-color: #fff
				position: absolute
				transform-origin: center

			&:before
				transform: rotate(45deg)

			&:after
				transform: rotate(-45deg)

		.back-btn
			width: 40px
			height: 40px
			position: relative
			display: flex
			align-items: center
			justify-content: center
			cursor: pointer

			.fal
				font-size: 21px
				color: #fff
				transition: all 300ms
				opacity: 0
				visibility: hidden

			&.show-back
				.fal
					opacity: 1
					visibility: visible

	.title
		font-family: 'Roboto Condensed', sans-serif
		color: #fff
		font-size: 28px
		font-weight: 700
		padding: 0 0 25px 0
		margin: 0

	.inputs
		display: flex
		width: 100%
		flex-direction: column
		margin-top: 20px
		padding: 25px

		&.no-top
			padding-top: 0
			margin-top: 0

	.buttons
		display: flex
		width: 100%
		flex-direction: row
		justify-content: space-between
		position: absolute
		top: 60px
		left: 0
		right: 0
		bottom: 0
		overflow: auto
		-webkit-overflow-scrolling: touch
		transition: all 300ms
		visibility: hidden
		opacity: 0
		
		&.show-buttons
			visibility: visible
			opacity: 1

		.main-btns
			display: flex
			flex-direction: column
			width: 100%

			.button
				height: 70px
				padding: 0 20px
				color: #fff
				display: flex
				align-items: center
				justify-content: flex-start
				font-size: 16px
				font-weight: 700
				font-family: 'Roboto Condensed', sans-serif
				margin-right: 1px
				cursor: pointer
				user-select: none
				transition: all 300ms
				border-bottom: 1px solid rgba(#fff, 0.2)
				gap: 15px

				.counter
					padding: 2px 8px
					border-radius: 20px
					background-color: #fff
					color: #000
					font-size: 14px

				&:hover
					background-color: rgba(#5AAE4B, 0.1)

				&.active
					color: #222
					background-color: #fff

					.counter
						background-color: #000
						color: #fff
				
				&.current-stage
					color: #fff

					&:before
						content: ''
						background-color: darken(#70C2FF, 15%)
						width: 8px
						height: 8px
						border-radius: 50%

				&.completed-stage
					color: #fff

					&:before
						content: ''
						background-color: #5AAE4B
						width: 8px
						height: 8px
						border-radius: 50%

				&.disabled
					color: rgba(#fff, 0.2)
					cursor: not-allowed

				&.delete-case-btn
					background-color: rgba(darken(#f00, 20%), 0.5)
					border-bottom: 0

					&:hover
						background-color: darken(#f00, 10%)

				


		.settings
			position: relative

			.label
				color: #fff
				font-size: 21px
				cursor: pointer
				padding: 0 15px

			.setting-contents
				padding: 10px
				background-color: #333
				position: absolute
				top: 60px
				right: 0
				display: none
				border-radius: 3px

			&.opened
				.setting-contents
					display: block

	.settings-panels
		position: absolute
		top: 60px
		left: 0
		right: 0
		bottom: 0
		overflow: auto
		-webkit-overflow-scrolling: touch
		visibility: hidden
		opacity: 0
		transition: all 300ms

		&.show-panel
			visibility: visible
			opacity: 1

</style>